/**
 * Service Sales Types
 * Types for service sales and budgets (orçamentos) management
 */

// Core Enums (matching database schema)
export type ServiceSaleType = 'budget' | 'sale'; // Matches database service_type enum
export type ServiceSaleStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'converted' | 'cancelled'; // Matches database budget_status enum
export type BudgetDiscountType = 'percentage' | 'fixed'; // Matches database budget_discount_type enum

// Additional business logic enums
export type InvoiceStatus = 'pending' | 'issued' | 'cancelled';
export type NegotiationType = 'all' | 'sales' | 'budgets';
export type PaymentMethod = 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'pix' | 'check' | 'other';
export type PaymentTerms = 'immediate' | '7_days' | '15_days' | '30_days' | '45_days' | '60_days' | 'custom';

/**
 * Service Sale - Main entity representing a sale or budget
 * Matches the updated service_sales table schema
 */
export interface ServiceSale {
  id: string;
  company_id: number; // Note: Database shows this as number, not string
  user_id: string;
  client_id?: string;

  // Basic Information
  type: ServiceSaleType;
  number?: string;
  issue_date?: string;
  expiry_date?: string;
  sale_date?: string;

  // Financial Information
  subtotal?: number;
  discount_type?: BudgetDiscountType;
  discount_value?: number;
  tax_amount?: number;
  total_value?: number;

  // Status and Workflow
  status?: ServiceSaleStatus;
  sent_date?: string;
  approved_date?: string;
  approved_by?: string;
  rejected_date?: string;
  rejected_by?: string;
  rejection_reason?: string;
  converted_date?: string;
  converted_to_transaction_id?: string;

  // Additional Fields (newly added)
  cost_center_id?: number;
  salesperson_id?: string;
  item_type?: 'service' | 'product';

  // Payment Information
  payment_method?: PaymentMethod;
  payment_terms?: PaymentTerms;
  payment_account_id?: string;

  // Notes and Terms
  notes?: string;
  terms_conditions?: string;
  client_notes?: string;
  internal_notes?: string;

  // Timestamps
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;

  // Joined fields (from related tables)
  client?: Client;
  cost_center?: CostCenter;
  salesperson?: User;
  items?: ServiceSaleItem[];
}

/**
 * Service Sale Item - Line items for service sales
 * Matches the updated service_sale_items table schema
 */
export interface ServiceSaleItem {
  id: string;
  service_sale_id: string;

  // Service Reference
  service_id?: string; // Links to industry_services

  // Item Details
  description: string; // Required - either custom or from service
  category_id?: number;
  quantity: number;
  unit?: string; // e.g., 'hora', 'dia', 'un', 'projeto'
  unit_price: number;

  // Discount and Tax
  discount_type?: BudgetDiscountType;
  discount_value?: number;
  tax_percentage?: number;
  total_amount?: number; // Generated column

  // Additional Information
  sort_order?: number;
  notes?: string;

  // Timestamps
  created_at?: string;
  updated_at?: string;

  // Joined fields
  service?: IndustryService;
  category?: Category;
}

/**
 * Service Sale Summary - Statistics for summary cards
 */
export interface ServiceSaleSummary {
  canceled: {
    count: number;
    value: number;
  };
  forecasted: {
    count: number;
    value: number;
  };
  approved: {
    count: number;
    value: number;
  };
  total: {
    count: number;
    value: number;
  };
}

/**
 * Service Sale Filters - Filter state for the sales page
 */
export interface ServiceSaleFilters {
  // Period filter
  periodType?: 'month' | 'year' | 'range';
  dateFrom?: Date;
  dateTo?: Date;
  
  // Search
  searchTerm?: string;
  
  // Negotiation type
  negotiationType?: NegotiationType;
  
  // Advanced filters
  saleType?: ServiceSaleType[];
  status?: ServiceSaleStatus[];
  
  // Sorting
  sortBy?: 'date' | 'number' | 'value' | 'client';
  sortOrder?: 'asc' | 'desc';
}

// Import related types - use forward references to avoid circular imports
import type { Client } from './contacts';
import type { CostCenter } from './cost-center';
import type { User } from './user';
import type { Category } from './category';
import type { IndustryService } from './industry/industry-services';

// Import Zod for validation schema types (optional - only used if Zod is available)
// Uncomment if Zod is installed in the project
// import type { z } from 'zod';

/**
 * Service Sale Form Data - For creating/editing sales
 * Matches the form structure used in the sales interface
 */
export interface ServiceSaleFormData {
  // Basic Information
  type: ServiceSaleType;
  client_id?: string;
  new_client?: Partial<ClientFormData>;
  sale_date: string;
  expiry_date?: string;

  // Additional Fields
  cost_center_id?: string; // String for form compatibility
  salesperson_id?: string;

  // Payment Information
  payment_method?: PaymentMethod;
  payment_terms?: PaymentTerms;
  payment_account_id?: string;

  // Financial Information (calculated)
  subtotal: number;
  discount_type: BudgetDiscountType;
  discount_value: number;
  tax_amount: number;
  total_value: number;

  // Notes and Terms
  notes?: string;
  terms_conditions?: string;
  client_notes?: string;
  internal_notes?: string;

  // Items
  items: ServiceSaleFormItem[];
}

/**
 * Service Sale Form Item - For items in the form
 */
export interface ServiceSaleFormItem {
  id?: string; // Optional for new items
  service_id?: string;
  description: string;
  category_id?: number;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_type?: BudgetDiscountType;
  discount_value: number;
  tax_percentage: number;
  total_amount: number;
  notes?: string;
}

/**
 * Client Form Data - Simplified for inline client creation
 */
export interface ClientFormData {
  name: string;
  document_type: 'cpf' | 'cnpj';
  document_number: string;
  email?: string;
  phone?: string;
  address_street?: string;
  address_number?: string;
  address_complement?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zip_code?: string;
  bank_name?: string;
  bank_agency?: string;
  bank_account?: string;
  bank_account_type?: 'corrente' | 'poupanca';
  payment_terms?: string;
  notes?: string;
  is_active?: boolean;
}

/**
 * Service Sales API Response Types
 */
export interface ServiceSalesResponse {
  data: ServiceSale[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateServiceSaleInput {
  type: ServiceSaleType;
  client_id?: string;
  sale_date: string;
  expiry_date?: string;
  cost_center_id?: number;
  salesperson_id?: string;
  notes?: string;
  terms_conditions?: string;
  client_notes?: string;
  internal_notes?: string;
  items: Omit<ServiceSaleItem, 'id' | 'service_sale_id' | 'created_at' | 'updated_at'>[];
}

export interface UpdateServiceSaleInput extends Partial<CreateServiceSaleInput> {
  id: string;
  status?: ServiceSaleStatus;
  rejection_reason?: string;
}

/**
 * Service Sale Validation Types
 */
export interface ServiceSaleValidation {
  isValid: boolean;
  errors: Record<string, string>;
  warnings: Record<string, string>;
}

/**
 * Service Sale Calculation Types
 */
export interface ServiceSaleCalculations {
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  items_count: number;
}

/**
 * Service Sale Table Row - Extended with display fields
 */
export interface ServiceSaleTableRow extends ServiceSale {
  // Display helpers
  formattedDate: string;
  formattedValue: string;
  statusBadge: {
    label: string;
    variant: 'default' | 'destructive' | 'secondary' | 'outline';
    color: string;
  };
  clientDisplayName?: string;
  costCenterName?: string;
  salespersonName?: string;
  itemsCount?: number;
}

/**
 * Service Sale Search and Filter Types
 */
export interface ServiceSaleSearchOptions {
  query?: string;
  client_id?: string;
  cost_center_id?: number;
  salesperson_id?: string;
  status?: ServiceSaleStatus[];
  type?: ServiceSaleType[];
  date_from?: string;
  date_to?: string;
  min_value?: number;
  max_value?: number;
}

/**
 * Service Sales Statistics Types
 */
export interface ServiceSalesStats {
  total_sales: number;
  total_budgets: number;
  approved_sales: number;
  pending_budgets: number;
  conversion_rate: number;
  average_ticket: number;
  total_value: number;
  monthly_trend: Array<{
    month: string;
    sales: number;
    budgets: number;
    value: number;
  }>;
}

/**
 * Service Sale Template Types
 */
export interface ServiceSaleTemplate {
  id: string;
  name: string;
  description?: string;
  company_id: string;
  user_id: string;
  items: ServiceSaleFormItem[];
  default_notes?: string;
  default_terms?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Service Sales Export Types
 */
export interface ServiceSalesExportOptions {
  format: 'excel' | 'pdf' | 'csv';
  filters?: ServiceSaleSearchOptions;
  include_items?: boolean;
  include_client_details?: boolean;
  date_range?: {
    start: string;
    end: string;
  };
}

/**
 * Service Sales Notification Types
 */
export interface ServiceSaleNotification {
  id: string;
  service_sale_id: string;
  type: 'status_change' | 'expiry_reminder' | 'approval_required' | 'rejection';
  recipient_id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

/**
 * React Hook Form Integration Types
 */

// Type for form field errors
export interface ServiceSaleFormErrors {
  type?: string;
  client_id?: string;
  sale_date?: string;
  expiry_date?: string;
  cost_center_id?: string;
  salesperson_id?: string;
  payment_method?: string;
  payment_account_id?: string;
  subtotal?: string;
  discount_value?: string;
  total_value?: string;
  'items'?: ServiceSaleFormItemErrors[];
}

export interface ServiceSaleFormItemErrors {
  description?: string;
  quantity?: string;
  unit_price?: string;
  discount_value?: string;
}

// Type for form submission state
export interface ServiceSaleFormState {
  isSubmitting: boolean;
  isDirty: boolean;
  isValid: boolean;
  draftSaving: boolean;
  lastSaved?: Date;
}

// Type for useFieldArray with items
export interface ServiceSaleUseFieldArray {
  fields: Partial<ServiceSaleFormItem>[];
  append: (item: Partial<ServiceSaleFormItem>) => void;
  remove: (index: number) => void;
  insert: (index: number, item: Partial<ServiceSaleFormItem>) => void;
  move: (from: number, to: number) => void;
  swap: (indexA: number, indexB: number) => void;
  update: (index: number, item: Partial<ServiceSaleFormItem>) => void;
  replace: (items: Partial<ServiceSaleFormItem>[]) => void;
}

/**
 * React Query Integration Types
 */

// Query keys for caching
export const serviceSaleQueryKeys = {
  all: ['service-sales'] as const,
  lists: () => [...serviceSaleQueryKeys.all, 'list'] as const,
  list: (filters: ServiceSaleFilters) => [...serviceSaleQueryKeys.lists(), filters] as const,
  details: () => [...serviceSaleQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceSaleQueryKeys.details(), id] as const,
  summaries: () => [...serviceSaleQueryKeys.all, 'summary'] as const,
  stats: (filters?: ServiceSaleFilters) => [...serviceSaleQueryKeys.all, 'stats', filters] as const,
} as const;

// Query options with proper typing
export interface ServiceSaleListQueryOptions {
  filters?: ServiceSaleFilters;
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
}

export interface ServiceSaleDetailQueryOptions {
  id: string;
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
}

// Mutation options
export interface UseCreateServiceSaleOptions {
  onSuccess?: (data: ServiceSale) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export interface UseUpdateServiceSaleOptions {
  onSuccess?: (data: ServiceSale) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export interface UseDeleteServiceSaleOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

/**
 * Zod Validation Schema Types (for reference)
 * These would be implemented with zod library - uncomment zod import to use
 */

// Uncomment zod import above to enable these types
/*
export interface ServiceSaleZodSchema {
  type: z.ZodEnum<['budget', 'sale']>;
  client_id: z.ZodOptional<z.ZodString>;
  sale_date: z.ZodString;
  expiry_date: z.ZodOptional<z.ZodString>;
  cost_center_id: z.ZodOptional<z.ZodString>;
  salesperson_id: z.ZodOptional<z.ZodString>;
  payment_method: z.ZodOptional<z.ZodEnum<PaymentMethod>>;
  payment_terms: z.ZodOptional<z.ZodEnum<PaymentTerms>>;
  payment_account_id: z.ZodOptional<z.ZodString>;
  subtotal: z.ZodNumber;
  discount_type: z.ZodEnum<BudgetDiscountType>;
  discount_value: z.ZodNumber;
  tax_amount: z.ZodNumber;
  total_value: z.ZodNumber;
  notes: z.ZodOptional<z.ZodString>;
  terms_conditions: z.ZodOptional<z.ZodString>;
  client_notes: z.ZodOptional<z.ZodString>;
  internal_notes: z.ZodOptional<z.ZodString>;
  items: z.ZodArray<ServiceSaleItemZodSchema>;
}

export interface ServiceSaleItemZodSchema {
  service_id: z.ZodOptional<z.ZodString>;
  description: z.ZodString;
  category_id: z.ZodOptional<z.ZodNumber>;
  quantity: z.ZodNumber;
  unit: z.ZodOptional<z.ZodString>;
  unit_price: z.ZodNumber;
  discount_type: z.ZodOptional<z.ZodEnum<BudgetDiscountType>>;
  discount_value: z.ZodOptional<z.ZodNumber>;
  tax_percentage: z.ZodOptional<z.ZodNumber>;
  total_amount: z.ZodOptional<z.ZodNumber>;
  notes: z.ZodOptional<z.ZodString>;
}
*/

/**
 * Additional Utility Types
 */

// Type for draft saving functionality
export interface ServiceSaleDraft {
  id?: string;
  formData: ServiceSaleFormData;
  timestamp: Date;
  autoSave: boolean;
}

// Type for status transitions
export interface ServiceSaleStatusTransition {
  from: ServiceSaleStatus;
  to: ServiceSaleStatus;
  action: string;
  requiresApproval?: boolean;
  allowedRoles?: string[];
}

// Type for commission calculation
export interface ServiceSaleCommission {
  salesperson_id: string;
  commission_rate: number; // percentage
  commission_amount: number;
  commission_type: 'percentage' | 'fixed';
  paid: boolean;
  paid_date?: string;
}

// Type for payment tracking
export interface ServiceSalePayment {
  id: string;
  service_sale_id: string;
  amount: number;
  payment_method: PaymentMethod;
  payment_date: string;
  status: 'pending' | 'paid' | 'cancelled' | 'refunded';
  transaction_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Utility Functions and Constants
 */

// Constants for display and validation
export const SERVICE_SALE_TYPE_LABELS: Record<ServiceSaleType, string> = {
  budget: 'Orçamento',
  sale: 'Venda'
};

export const SERVICE_SALE_STATUS_LABELS: Record<ServiceSaleStatus, string> = {
  draft: 'Rascunho',
  sent: 'Enviado',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  expired: 'Expirado',
  converted: 'Convertido',
  cancelled: 'Cancelado'
};

export const BUDGET_DISCOUNT_TYPE_LABELS: Record<BudgetDiscountType, string> = {
  percentage: 'Percentual',
  fixed: 'Fixo'
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Dinheiro',
  bank_transfer: 'Transferência Bancária',
  credit_card: 'Cartão de Crédito',
  debit_card: 'Cartão de Débito',
  pix: 'PIX',
  check: 'Cheque',
  other: 'Outro'
};

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  immediate: 'À Vista',
  '7_days': '7 dias',
  '15_days': '15 dias',
  '30_days': '30 dias',
  '45_days': '45 dias',
  '60_days': '60 dias',
  custom: 'Personalizado'
};

// Status colors for UI components
export const SERVICE_SALE_STATUS_COLORS: Record<ServiceSaleStatus, string> = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  sent: 'bg-blue-100 text-blue-800 border-blue-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  expired: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  converted: 'bg-purple-100 text-purple-800 border-purple-200',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Validation functions
export const validateServiceSaleItem = (item: ServiceSaleFormItem): string[] => {
  const errors: string[] = [];

  if (!item.description?.trim()) {
    errors.push('Descrição é obrigatória');
  }

  if (!item.quantity || item.quantity <= 0) {
    errors.push('Quantidade deve ser maior que zero');
  }

  if (!item.unit_price || item.unit_price <= 0) {
    errors.push('Preço unitário deve ser maior que zero');
  }

  if (item.discount_value && item.discount_value < 0) {
    errors.push('Valor do desconto não pode ser negativo');
  }

  if (item.discount_type && !item.discount_value) {
    errors.push('Tipo de desconto requer valor do desconto');
  }

  return errors;
};

export const validateServiceSale = (sale: ServiceSaleFormData): string[] => {
  const errors: string[] = [];

  if (!sale.type) {
    errors.push('Tipo é obrigatório');
  }

  if (!sale.client_id && !sale.new_client) {
    errors.push('Cliente é obrigatório');
  }

  if (!sale.sale_date) {
    errors.push('Data da venda é obrigatória');
  }

  if (sale.type === 'budget' && !sale.expiry_date) {
    errors.push('Data de validade é obrigatória para orçamentos');
  }

  if (!sale.items || sale.items.length === 0) {
    errors.push('Adicione pelo menos um item');
  } else {
    sale.items.forEach((item, index) => {
      const itemErrors = validateServiceSaleItem(item);
      itemErrors.forEach(error => {
        errors.push(`Item ${index + 1}: ${error}`);
      });
    });
  }

  if (sale.subtotal < 0) {
    errors.push('Subtotal não pode ser negativo');
  }

  if (sale.total_value < 0) {
    errors.push('Valor total não pode ser negativo');
  }

  return errors;
};

// Calculation functions
export const calculateItemTotal = (item: ServiceSaleFormItem): number => {
  const subtotal = (item.quantity || 0) * (item.unit_price || 0);
  let discount = 0;

  if (item.discount_type === 'percentage' && item.discount_value) {
    discount = subtotal * (item.discount_value / 100);
  } else if (item.discount_type === 'fixed' && item.discount_value) {
    discount = item.discount_value;
  }

  const afterDiscount = Math.max(0, subtotal - discount);
  const taxAmount = afterDiscount * ((item.tax_percentage || 0) / 100);

  return afterDiscount + taxAmount;
};

export const calculateSaleTotal = (items: ServiceSaleFormItem[]): {
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  total: number;
} => {
  let subtotal = 0;
  let discountAmount = 0;
  let taxAmount = 0;

  items.forEach(item => {
    const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
    subtotal += itemSubtotal;

    let itemDiscount = 0;
    if (item.discount_type === 'percentage' && item.discount_value) {
      itemDiscount = itemSubtotal * (item.discount_value / 100);
    } else if (item.discount_type === 'fixed' && item.discount_value) {
      itemDiscount = item.discount_value;
    }

    discountAmount += itemDiscount;

    const afterDiscount = Math.max(0, itemSubtotal - itemDiscount);
    taxAmount += afterDiscount * ((item.tax_percentage || 0) / 100);
  });

  const total = Math.max(0, subtotal - discountAmount + taxAmount);

  return {
    subtotal,
    discountAmount,
    taxAmount,
    total
  };
};

// Status transition validation
export const isValidStatusTransition = (
  from: ServiceSaleStatus,
  to: ServiceSaleStatus
): boolean => {
  const validTransitions: Record<ServiceSaleStatus, ServiceSaleStatus[]> = {
    draft: ['sent', 'cancelled'],
    sent: ['approved', 'rejected', 'cancelled', 'expired'],
    approved: ['converted', 'cancelled'],
    rejected: ['draft', 'cancelled'],
    expired: ['cancelled'],
    converted: ['cancelled'],
    cancelled: [] // Terminal state
  };

  return validTransitions[from]?.includes(to) || false;
};

// Format functions for display
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

export const formatServiceSaleNumber = (type: ServiceSaleType, number: string, year: number): string => {
  const prefix = type === 'budget' ? 'ORC' : 'VND';
  const paddedNumber = number.padStart(6, '0');
  return `${prefix}-${paddedNumber}/${year}`;
};

export const getServiceSaleDisplayInfo = (sale: ServiceSale): {
  formattedNumber: string;
  formattedTotal: string;
  statusBadge: {
    label: string;
    color: string;
  };
  typeLabel: string;
} => {
  const year = new Date(sale.sale_date || sale.created_at || '').getFullYear();
  const formattedNumber = sale.number
    ? formatServiceSaleNumber(sale.type, sale.number, year)
    : `${sale.type === 'budget' ? 'Orçamento' : 'Venda'} sem número`;

  const formattedTotal = formatCurrency(sale.total_value || 0);

  const statusBadge = {
    label: SERVICE_SALE_STATUS_LABELS[sale.status || 'draft'],
    color: SERVICE_SALE_STATUS_COLORS[sale.status || 'draft']
  };

  const typeLabel = SERVICE_SALE_TYPE_LABELS[sale.type];

  return {
    formattedNumber,
    formattedTotal,
    statusBadge,
    typeLabel
  };
};

